import { DeletedFileView, IMAGE_MAX_BYTES, ImageMimeType, StorageKeyQuery, StorageListQuery, StoredFilePage, StoredFileView, UploadImageFields, UploadVideoFields, VIDEO_MAX_BYTES, VideoMimeType, } from '../../storage.js';
/**
 * Media upload (FR-POST-02, FR-REEL-01) — the path that unblocks the video
 * attachment kind `content.ts` declares but the scaffold left unwired.
 *
 * Every route here is authenticated: uploads cost storage and bandwidth, and
 * an anonymous writer to a public bucket is a free CDN for whoever finds it.
 * Deletion additionally checks the key's owner segment, so holding a key is
 * not the same as being allowed to remove it.
 *
 * Both uploads take an optional `folder` text part, so an app can file
 * avatars, post images and reels apart from each other inside the member's own
 * space. The folder is free text but is VALIDATED against a fixed grammar
 * rather than sanitised — see `StorageFolder`. It sits AFTER the owner segment
 * in the key, never before it, so no folder name can move a file out of the
 * member it belongs to and `parseObjectKey` still reads the owner from the
 * same place.
 *
 * The two upload routes are `multipart/form-data`, the only routes in either
 * manifest that are not JSON. They are separate endpoints rather than one
 * `/storage/files` taking a kind, because the two differ in every rule that
 * matters — accepted types, size ceiling, and whether a duration applies —
 * and a single endpoint would have to read the discriminator out of the
 * request before it could decide which limits to enforce.
 */
export const storageRoutes = {
    uploadImage: {
        method: 'POST',
        path: '/storage/images',
        upload: {
            field: 'file',
            accepts: ImageMimeType.options,
            maxBytes: IMAGE_MAX_BYTES,
            fields: UploadImageFields,
        },
        response: StoredFileView,
    },
    /**
     * A Reel's video. `durationSeconds` rides along as a text part and is
     * bounded by the contract — see `UploadVideoFields` for why that is a
     * declaration and not an enforcement.
     */
    uploadVideo: {
        method: 'POST',
        path: '/storage/videos',
        upload: {
            field: 'file',
            accepts: VideoMimeType.options,
            maxBytes: VIDEO_MAX_BYTES,
            fields: UploadVideoFields,
        },
        response: StoredFileView,
    },
    /**
     * Read one object back. The bucket is public-read, so the caller could
     * assemble this URL themselves — the endpoint exists so they do not have to
     * hardcode the CDN host, and so a key that 404s is discovered here rather
     * than by a broken image in a feed.
     *
     * The key is a query parameter rather than a path segment because keys
     * contain slashes; `/storage/files/images/acct-1/....jpg` would need a
     * wildcard route and would then accept path shapes the contract cannot
     * describe.
     */
    file: {
        method: 'GET',
        path: '/storage/files',
        query: StorageKeyQuery,
        response: StoredFileView,
    },
    /**
     * Browse the caller's own uploads, optionally narrowed to one kind or one
     * folder. Declared before `remove` for readability only — the paths differ,
     * so no ordering constraint applies the way it does on the profile routes.
     *
     * There is no parameter for whose files to list, and that is the design: the
     * prefix is built from the token's subject, so the endpoint cannot be aimed
     * at another member. Moderator access to someone else's uploads belongs in
     * the admin manifest behind a permission (ADR-22), not here.
     */
    list: {
        method: 'GET',
        path: '/storage/files/list',
        query: StorageListQuery,
        response: StoredFilePage,
    },
    remove: {
        method: 'DELETE',
        path: '/storage/files',
        query: StorageKeyQuery,
        response: DeletedFileView,
    },
};
//# sourceMappingURL=routes.js.map